export const userListDocumentation = `
## Table Columns

| Column | Width | Filter Type | Permission Required | Hideable | Description |
|--------|-------|-------------|-------------------|----------|-------------|
| First Name | 200px | Text | - | <span class="checkmark">✓</span> | User's first name |
| Last Name | 200px | Text | - | <span class="checkmark">✓</span> | User's last name |
| Role | 200px | Text | - | <span class="checkmark">✓</span> | User role (provider/assistant) |
| Email | 220px | Text | - | <span class="checkmark">✓</span> | User's email address used for login |
| Custom Tags | 200px | Select (bubbles) | ACCOUNTS_GET (DS only) | <span class="checkmark">✓</span> | Account-level custom tags displayed as colored bubbles |
| Account | 200px | Select with dynamic hint | ACCOUNTS_GET (DS only) | <span class="checkmark">✓</span> | Business name of the account |
| Custom ID | 200px | Text | ACCOUNTS_GET (DS only) | <span class="crossmark">✗</span> | Account custom identifier (not hideable) |
| Access Granted | 240px | Checkbox (Allowed/Not Allowed) | ACCOUNTS_GET (DS only) | <span class="checkmark">✓</span> | Indicates if user has access to specific accounts |

## Account Type Dependencies

### Distribution Sponsors
- See additional account-related columns when viewing users across multiple accounts
- Different table key used: \`usersDSTable\`
- Can manage users in child accounts with proper navigation context
- Permission checks differ for child account users vs current account

### Regular Accounts
- Table key: \`usersTable\`
- Standard column visibility (4 base columns only)
- Full user management capabilities within their account
- No cross-account user visibility

## Filtering and Search

### Text Filters
- **firstName**: Partial text matching
- **lastName**: Partial text matching  
- **email**: Partial text matching

### Select Filters (DS Accounts Only)
- **customTags**: Multi-select dropdown with colored options
- **businessName**: Dynamic select with hint functionality
- **isAllowed**: Checkbox filter (Allowed/Not Allowed/All)

## Row Actions

| Action | Icon | Permission Required | Description |
|--------|------|-------------------|-------------|
| Edit User | Edit | ACCOUNT_USER_PATCH | Opens user edit form for updating user information. Always available in row menu. |
| Resend Invitation | Resend | - | Resends email invitation to the user. Only shown when \`canResendInvitation\` is true. Has divider above in menu. |
| Remove User | Remove | ACCOUNT_USER_DELETE | Deletes the user after confirmation dialog. Shows user's full name in confirmation. Error styling in menu. |

### Navigation Logic
- **Standard Account**: Uses \`ALL_ROUTES.user.edit\` route
- **DS Child Account**: Uses \`ALL_ROUTES.user.editDS\` route with account context

## Permissions

### User List Access
Required permission: \`ACCOUNT_USERS_GET\`

### User Creation
Required permission: \`ACCOUNT_USER_CREATE\`
- Add User button visible when permission exists

### User Editing
Required permission: \`ACCOUNT_USER_PATCH\`
- Edit action available in row menu

### User Deletion
Required permission: \`ACCOUNT_USER_DELETE\`
- Remove action available in row menu

### Distribution Sponsor Features
Required permission: \`ACCOUNTS_GET\`
- Enables multi-account user management
- Shows additional account-related columns
- Allows cross-account user access

## API Endpoints

| Method | Endpoint | Description |
|--------|----------|-------------|
| GET | \`/accounts/{accountUuid}/users\` | List users with filtering and pagination |
| POST | \`/accounts/{accountUuid}/users/{userUuid}/invitation\` | Resend user invitation |
| DELETE | \`/accounts/{accountUuid}/users/{userUuid}\` | Delete user |

## Request/Response Examples

### List Users (GET)
**Request:**
\`\`\`
GET /accounts/{accountUuid}/users?fields=firstName,lastName,role,email&page=0&size=20&sort=firstName,asc
\`\`\`

**Success Response (200 OK):**
\`\`\`json
{
  "content": [
    {
      "uuid": "user-uuid-1",
      "firstName": "John",
      "lastName": "Doe", 
      "role": "provider",
      "email": "john.doe@example.com",
      "canResendInvitation": true
    },
    {
      "uuid": "user-uuid-2",
      "firstName": "Jane",
      "lastName": "Smith",
      "role": "assistant", 
      "email": "jane.smith@example.com",
      "canResendInvitation": false
    }
  ],
  "totalElements": 25,
  "totalPages": 2,
  "size": 20,
  "number": 0
}
\`\`\`

### Resend Invitation (POST)
**Request:**
\`\`\`
POST /accounts/{accountUuid}/users/{userUuid}/invitation
\`\`\`

**Success Response (200 OK):**
\`\`\`json
{
  "message": "Invitation sent successfully"
}
\`\`\`

**Error Response (500 Internal Server Error - Too Many Invitations):**
\`\`\`json
{
  "errorCode": 1,
  "message": "Too many invitations have been sent",
  "correlationUUID": "correlation-id"
}
\`\`\`

### Delete User (DELETE)
**Success Response (204 No Content):**
No response body

**Error Response (404 Not Found):**
\`\`\`json
{
  "message": "User not found",
  "correlationUUID": "correlation-id"
}
\`\`\`

**Error Response (500 Internal Server Error - Cannot Remove):**
\`\`\`json
{
  "errorCode": 1,
  "message": "User cannot be removed",
  "correlationUUID": "correlation-id"
}
\`\`\`

## Table Features

### Sorting
- **Default**: firstName ascending
- **Available**: All displayed columns are sortable
- **Multi-sort**: Support for secondary sorting

### Pagination
- **Type**: Virtual scrolling with infinite loading
- **Page Size**: Configurable (default: 20)
- **Loading**: Shows shimmer during data fetch

### Filtering
- **Persistence**: Filters persist in browser session
- **Real-time**: Results update as user types
- **Clear**: Filters can be cleared individually or all at once

### Row Interaction
- **Click**: Navigate to user edit form
- **Menu**: Right-click or menu button for actions
- **Selection**: Single row selection for actions

## Business Rules

### Distribution Sponsor Logic
1. **Account Context**: DS users can manage users across multiple child accounts
2. **Route Selection**: Different routes for own account vs child account management
3. **Field Visibility**: Additional columns shown for account identification
4. **Access Control**: \`isAllowed\` field indicates if user has access to specific accounts

### Invitation Management
1. **Resend Availability**: Based on \`canResendInvitation\` flag from backend
2. **Rate Limiting**: System prevents too many invitations (error code 1)
3. **Success Feedback**: Toast notification confirms invitation sent

### User Removal
1. **Confirmation**: Always requires user confirmation via dialog
2. **Business Constraints**: Some users cannot be removed (error code 1)
3. **Cascade Effects**: System handles related data cleanup
4. **Success Feedback**: Toast notification with user name confirmation

## Error Handling

### Common Error Scenarios
1. **403 Forbidden**: Insufficient permissions for user list access
2. **404 Not Found**: User not found during action attempts
3. **500 Internal Server Error**: Business rule violations or system errors
4. **Network Errors**: Connection issues, timeout handling

### Error Recovery
- **Retry Mechanisms**: Automatic retry for transient failures
- **User Feedback**: Clear error messages with actionable guidance
- **Graceful Degradation**: Partial functionality when some features fail

## Route Structure

### Standard Account Routes
- **List**: \`/{currentAccountUuid}/users\`
- **Create**: \`/{currentAccountUuid}/users/create\`
- **Edit**: \`/{currentAccountUuid}/users/{userUuid}/edit\`

### Distribution Sponsor Routes
- **List**: \`/{currentAccountUuid}/accounts/{accountUuid}/users\`
- **Create**: \`/{currentAccountUuid}/accounts/{accountUuid}/users/create\`
- **Edit**: \`/{currentAccountUuid}/accounts/{accountUuid}/users/{userUuid}/edit\`
`;